import { useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { TransactionWarnings, formatBalance, formatDate } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  setBlacklistData,
  setBlacklistDataVisible,
  setBlacklistDataItem,
  setBlacklistItemVisible,
  changePanelState,
} from '../store/slices/outgoing-transaction';
import {
  useLazyGetProblemTransactionSenderClientsOfficiasQuery,
  useCreateProblemTransactionChatMutation,
  useLazyGetCustomerOutgoingTransactionWarningQuery,
} from '../store/services/customers';

const OutgoingTransactionWarning = (): React.JSX.Element => {
  const {
    customersProblemTransactions: {
      outgoing: { warnings, senderCompanyOfficials, transaction, chats },
    },
    customersDictionaries: {
      customersTransactionTypes,
      currentEmployee,
      currenciesList,
    },
  } = useAppSelector((state) => state);

  const [getSenderAccounts] =
    useLazyGetProblemTransactionSenderClientsOfficiasQuery();

  const [createTransactionChat, { isLoading: chatCreateInProgress }] =
    useCreateProblemTransactionChatMutation();

  const [
    getOutgoingTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetCustomerOutgoingTransactionWarningQuery();

  const dispatch = useAppDispatch();

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { transactionId } = useParams();

  const getOutgoingTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    await getOutgoingTransactionWarningById({
      id,
      warningId,
    });
  };

  const createChatWithMessage = async ({
    message,
    userId,
  }: {
    message: string;
    userId?: string;
  }) => {
    const title = `RFI: Outgoing transaction ${formatBalance(
      transaction?.sum || 0,
      currenciesList?.find((item) => item.id === transaction?.currencyId),
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    const body: {
      title: string;
      employeeId: string;
      organizationId: string;
      userId?: string;
    } = {
      title,
      employeeId: currentEmployee?.id as string,
      organizationId: transaction?.senderAccount?.organizationId,
    };

    if (userId) {
      body.userId = userId;
    }

    const response = (await createTransactionChat({
      id: transactionId as string,
      body,
    })) as { data: any };

    if (response && response.data && sendMessage) {
      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );
      sendMessage(
        JSON.stringify({
          event: 'message',
          data: {
            channelId: response?.data?.data?.id,
            content: message,
          },
        }),
      );
      sendMessage(
        JSON.stringify({
          event: 'unsubscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );
    }
  };

  useEffect(() => {
    if (
      transaction &&
      transaction.senderAccount &&
      transaction.senderAccount?.organization?.type === 'CORPORATE' &&
      !senderCompanyOfficials
    ) {
      getSenderAccounts(transaction.senderAccount.organizationId);
    }
  }, [transaction, senderCompanyOfficials]);

  return (
    <TransactionWarnings
      warnings={warnings}
      employee={undefined}
      transactionsTypes={customersTransactionTypes}
      setBlacklistDataVisible={(visible) => {
        dispatch(setBlacklistDataVisible(visible));
        dispatch(changePanelState({ state: false }));
      }}
      setBlacklistData={(data) => dispatch(setBlacklistData(data))}
      setBlacklistItemVisible={(visible) => {
        dispatch(setBlacklistItemVisible(visible));
        dispatch(changePanelState({ state: false }));
      }}
      setBlacklistDataItem={(data) => dispatch(setBlacklistDataItem(data))}
      transactionUrlType="outgoing"
      transactionId={transactionId}
      companyOfficials={senderCompanyOfficials}
      individualCustomer={transaction?.senderAccount}
      chats={chats}
      createChatWithMessage={createChatWithMessage}
      chatCreateInProgress={chatCreateInProgress}
      getWarningById={getOutgoingTransactionWarningByIdAction}
      isWarningByIdLoading={isWarningByIdLoading}
      isWarningByIdFetching={isWarningByIdFetching}
      withTransactionUrl
    />
  );
};

export default OutgoingTransactionWarning;
